import { AppBar, Tabs, Tab } from '@mui/material';
import { useQuery } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Page, PageTitleWithBack } from '../../../components';
import {
  getPlanGeneralInfo,
  getPlanDetails,
  getPlanElevations,
  getPlanRooms
} from '../../../fetch';
import { IPlanGeneralInfo, IPlanDetails, IPlanElevations } from '../../../models';
import { GeneralInformation } from './general-information';
import { PlanDetails } from './plan-details';
import { ElevationDetails } from './elevation-details';
import { PlanRooms } from './plan-rooms';

export const enum planPages {
  genInfo,
  details,
  elevation,
  rooms
}
interface IAddEditPlan {}

export const AddEditPlan: FC<IAddEditPlan> = () => {
  const classes = useStyles();
  const [planName, setPlanName] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<planPages>(planPages.genInfo);
  const [selectedTab, setSelectedTab] = useState(window.location.pathname);
  const [isDirtyForm, setDirtyForm] = useState<boolean>(false);
  const { planId }: { planId: string } = useParams();
  const history = useHistory();

  const {
    isLoading: isLoadingGeneralInfo,
    data: generalInfo,
    refetch: refetchGeneralInfo
  } = useQuery<IPlanGeneralInfo, Error>(
    ['plan-general-info', planId],
    () => getPlanGeneralInfo(planId),
    {
      onSuccess: d => setPlanName(d.planName),
      enabled: planId !== 'add'
    }
  );

  const {
    isLoading: isLoadingDetails,
    data: details,
    refetch: refetchDetails
  } = useQuery<IPlanDetails, Error>(['plan-details', planId], () => getPlanDetails(planId), {
    onSuccess: d => setPlanName(d.planName),
    enabled: planId !== 'add'
  });

  const {
    isLoading: isLoadingElevations,
    data: elevations,
    refetch: refetchElevations
  } = useQuery<IPlanElevations[], Error>(
    ['plan-elevations', planId],
    () => getPlanElevations(planId),
    {
      enabled: planId !== 'add'
    }
  );

  const {
    isLoading: isLoadingRooms,
    data: rooms,
    refetch: refetchRooms
  } = useQuery<number[], Error>(['rooms', planId], () => getPlanRooms(planId), {
    enabled: planId !== 'add'
  });

  useEffect(() => {
    if (window.location.href.includes('general-information')) {
      setSelectedPage(planPages.genInfo);
    }
    if (window.location.href.includes('plan-details')) {
      setSelectedPage(planPages.details);
    }
    if (window.location.href.includes('elevation-details')) {
      setSelectedPage(planPages.elevation);
    }
    if (window.location.href.includes('plan-rooms')) {
      setSelectedPage(planPages.rooms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, planId]);

  const getPlanId = () => {
    return planId ? planId : 'add';
  };
  const planLinks = useMemo(
    () =>
      [
        {
          name: 'General Information',
          url: `/development/plans/manage-plans/${getPlanId()}/general-information`
        },
        {
          name: 'Plan Details',
          url: `/development/plans/manage-plans/${getPlanId()}/plan-details`
        },
        {
          name: 'Elevation Details',
          url: `/development/plans/manage-plans/${getPlanId()}/elevation-details`
        },
        {
          name: 'Plan Rooms',
          url: `/development/plans/manage-plans/${getPlanId()}/plan-rooms`
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planId]
  );

  const showLoader =
    isLoadingGeneralInfo || isLoadingDetails || isLoadingElevations || isLoadingRooms;
  const title = showLoader
    ? 'Loading...'
    : !planName && planId === 'add'
    ? 'Add Plan'
    : planName || 'Edit Plan';

  return (
    <Page title={title}>
      <>
        <PageTitleWithBack
          title={title}
          backText="Back to plans"
          backUrl="/development/plans/manage-plans"
        />
        {planId !== 'add' && (
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_, value) => {
                if (!isDirtyForm) {
                  setSelectedTab(value);
                  history.push(value);
                } else {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    setSelectedTab(value);
                    history.push(value);
                  } else {
                    return;
                  }
                }
              }}
              variant="scrollable"
            >
              {planLinks.map(link => {
                return (
                  <Tab
                    value={link.url}
                    key={link.name}
                    id={`${link.name}-information`}
                    aria-controls={`${link.name}-information`}
                    label={link.name}
                  />
                );
              })}
            </Tabs>
          </AppBar>
        )}
        {selectedPage === planPages.genInfo && (
          <GeneralInformation
            setDirtyForm={setDirtyForm}
            generalInfo={generalInfo}
            isLoadingGeneralInfo={isLoadingGeneralInfo}
            refetchGeneralInfo={refetchGeneralInfo}
          />
        )}
        {selectedPage === planPages.details && (
          <PlanDetails
            setDirtyForm={setDirtyForm}
            details={details}
            isLoadingDetails={isLoadingDetails}
            refetchDetails={refetchDetails}
          />
        )}
        {selectedPage === planPages.elevation && (
          <ElevationDetails
            setDirtyForm={setDirtyForm}
            elevations={elevations}
            isLoadingElevations={isLoadingElevations}
            refetchElevations={refetchElevations}
          />
        )}
        {selectedPage === planPages.rooms && (
          <PlanRooms
            setDirtyForm={setDirtyForm}
            rooms={rooms}
            isLoadingRooms={isLoadingRooms}
            refetchRooms={refetchRooms}
          />
        )}
      </>
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  }
}));
